import React from 'react';

import { useIntl } from 'react-intl';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import ArrowLeftIcon from '@atlaskit/icon/core/arrow-left';
import CrossIcon from '@atlaskit/icon/core/cross';
import EditIcon from '@atlaskit/icon/core/edit';
import MenuIcon from '@atlaskit/icon/core/menu';
import { ExitingPersistence, SlideIn } from '@atlaskit/motion';
import { Flex, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { type Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { chatHeaderTestIds } from '../../common/constants/constants';

import { chatHeaderMessages as msg } from './messages';

const styles = cssMap({
	container: {
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		width: '100%',
		position: 'relative', // important to create a new stacking context for spotlights and menus
		top: token('space.0'),
		borderRadius: 'inherit',
	},
	leftContainer: {
		flex: 1,
		alignItems: 'center',
		justifyContent: 'flex-start',
		minWidth: '0px',
	},
	inlineContainer: {
		maxWidth: '100%',
	},
	rightContainer: {
		alignItems: 'center',
		justifyContent: 'flex-end',
	},
});

export type ChatHeaderProps = {
	selectedAgent?: Agent;
	agentTitleComponent?: React.ReactNode;
	/** Optional control rendered immediately before the switch-mode component (e.g. expand to split layout). */
	splitLayoutToggleComponent?: React.ReactNode;
	switchModeComponent?: React.ReactNode;
	moreMenuComponent?: React.ReactNode;
	onMenuClick?: () => void;
	menuButtonRef?: React.Ref<HTMLButtonElement>;
	onNewChatClick?: () => void;
	onCloseClick?: () => void;
	onBackClick?: () => void;
	/** When true, autofocuses the back button on mount. */
	autoFocusBackButton?: boolean;
	/** When provided, overrides the default back button label (e.g. "Go back to Insights" when viewing an insight chat). */
	backButtonLabel?: string;
	isNewChatButtonDisabled?: boolean;
};

export const ChatHeader = ({
	selectedAgent,
	agentTitleComponent,
	splitLayoutToggleComponent,
	switchModeComponent,
	moreMenuComponent,
	onMenuClick,
	menuButtonRef,
	onNewChatClick,
	onCloseClick,
	onBackClick,
	autoFocusBackButton,
	backButtonLabel,
	isNewChatButtonDisabled = false,
}: ChatHeaderProps): React.JSX.Element => {
	const { formatMessage: fm } = useIntl();

	const isDefaultAgent = !selectedAgent || selectedAgent.is_default;
	const shouldShowMenuButton = typeof onMenuClick === 'function';
	const shouldShowNewChatButton = typeof onNewChatClick === 'function';
	const shouldShowCloseButton = typeof onCloseClick === 'function';
	const shouldShowBackButton = typeof onBackClick === 'function';

	return (
		<Flex
			xcss={styles.container}
			testId={chatHeaderTestIds.container}
			direction="row"
			role="banner"
		>
			<Flex xcss={styles.leftContainer}>
				<Inline alignBlock="center" space="space.050" xcss={styles.inlineContainer}>
					<ExitingPersistence>
						{shouldShowBackButton && (
							<SlideIn
								enterFrom="left"
								exitTo="left"
								fade="in"
								animationTimingFunction="ease-out"
								duration="medium"
							>
								{(animationProps) => (
									<div {...animationProps}>
										<IconButton
											testId={chatHeaderTestIds.backButton}
											appearance="subtle"
											isTooltipDisabled={false}
											autoFocus={autoFocusBackButton}
											label={backButtonLabel ?? fm(msg.chatHeaderBackButtonLabel)}
											icon={ArrowLeftIcon}
											onClick={onBackClick}
										/>
									</div>
								)}
							</SlideIn>
						)}
					</ExitingPersistence>
					{shouldShowMenuButton && (
						<IconButton
							testId={chatHeaderTestIds.menuButton}
							appearance="subtle"
							isTooltipDisabled={false}
							ref={menuButtonRef}
							label={fm(msg.chatHeaderMenuButtonLabel)}
							icon={MenuIcon}
							onClick={onMenuClick}
						/>
					)}

					{agentTitleComponent}
				</Inline>
			</Flex>

			<Flex xcss={styles.rightContainer}>
				<Inline space="space.050">
					{shouldShowNewChatButton && (
						<IconButton
							testId={chatHeaderTestIds.newChatButton}
							appearance="subtle"
							isTooltipDisabled={false}
							label={
								isDefaultAgent
									? fm(msg.chatHeaderNewChatButtonLabel)
									: fm(msg.chatHeaderNewAgentChatButtonLabel, { agentName: selectedAgent.name })
							}
							icon={EditIcon}
							onClick={onNewChatClick}
							isDisabled={isNewChatButtonDisabled}
						/>
					)}
					{splitLayoutToggleComponent}
					{switchModeComponent}
					{moreMenuComponent}
					{shouldShowCloseButton && (
						<IconButton
							testId={chatHeaderTestIds.closeButton}
							appearance="subtle"
							isTooltipDisabled={false}
							label={fm(msg.chatHeaderCloseButton)}
							icon={CrossIcon}
							onClick={onCloseClick}
						/>
					)}
				</Inline>
			</Flex>
		</Flex>
	);
};
